import React, { useMemo, useState } from "react";
import {
  Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Box, Chip, Skeleton,
  Typography, Select, MenuItem, Button, Switch, Tooltip,
  TablePagination, Checkbox, Stack
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";

const fmtDateTime = (v) => {
  if (!v) return "-";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
};

const IntegrationTable = ({
  integrations = [],
  rowCount = 0,
  page = 0,                 // zero-based
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading = false,
  onEdit,
  onDelete,
  onToggleStatus,
  onTest,
  // bulk
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
}) => {
  const [localSearch, setLocalSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  const ordered = useMemo(
    () => integrations.slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    [integrations]
  );

  const isRowSelected = (id) => selected.has(id);
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const onPageIds = ordered.map((r) => r.id);
  const allOnPageSelected = ordered.length > 0 && ordered.every((r) => selected.has(r.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) onPageIds.forEach((id) => next.delete(id));
      else onPageIds.forEach((id) => next.add(id));
      return next;
    });

  const selectedArr = Array.from(selected);

  return (
    <Paper variant="outlined">
      {/* Busca */}
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          placeholder="Buscar"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch?.(localSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Barra de ações em massa */}
      {selectedArr.length > 0 && (
        <Box
          sx={{
            mx: 1.5,
            mb: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2">{selectedArr.length} selecionada(s)</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button size="small" onClick={() => onBulkActivate?.(selectedArr)}>Ativar</Button>
            <Button size="small" onClick={() => onBulkDeactivate?.(selectedArr)}>Desativar</Button>
            <Button size="small" color="error" onClick={() => onBulkDelete?.(selectedArr)}>Excluir</Button>
          </Box>
        </Box>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48}>
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={!allOnPageSelected && selectedArr.length > 0}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>Ordem</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Provedor</TableCell>
              <TableCell>Tipo Autenticação</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Últ. Teste</TableCell>
              <TableCell>Observações</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3, 4, 5, 6].map((row) => (
                  <TableRow key={row}>
                    <TableCell><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={32} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width="60%" height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={64} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={56} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={64} height={20} /></TableCell>
                    <TableCell align="right"><Skeleton variant="rounded" width={80} height={28} sx={{ ml: "auto" }} /></TableCell>
                  </TableRow>
                ))}
              </>
            ) : ordered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma integração encontrada.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              ordered.map((row) => {
                const checked = isRowSelected(row.id);
                return (
                  <TableRow key={row.id} hover selected={checked}>
                    <TableCell>
                      <Checkbox checked={checked} onChange={() => toggleSelect(row.id)} />
                    </TableCell>
                    <TableCell>{row.ordem ?? ""}</TableCell>
                    <TableCell>{row.nome}</TableCell>
                    <TableCell><Chip size="small" label={row.provedor || "-"} /></TableCell>
                    <TableCell>{row.tipo_auth || "-"}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch
                          size="small"
                          checked={!!row.status}
                          onChange={() => onToggleStatus?.(row)}
                        />
                        <Chip
                          size="small"
                          label={row.status ? "Ativa" : "Inativa"}
                          color={row.status ? "success" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>{fmtDateTime(row.last_test_at || row.last_sync_at)}</TableCell>
                    <TableCell>{row.observacoes || "-"}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Testar conexão">
                        <IconButton size="small" onClick={() => onTest?.(row)}>
                          <PlayArrowRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => onEdit?.(row)}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" color="error" onClick={() => onDelete?.(row.id)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">Linhas por página:</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((sz) => (
              <MenuItem key={sz} value={sz}>{sz}</MenuItem>
            ))}
          </Select>
        </Box>

        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Paper>
  );
};

export default IntegrationTable;
